import { _, FreezerService, managedAjaxUtil, IAjaxState, moment } from 'Imports';
import {
    VideoEventResponseIEnumerablePagedResponseBase,
    VideoEventFilterRequest,
    VideoEventApiFactory,
    VideoEventResponse,
    VideoEventWorkflowStatusIdEnum,
} from '$Generated/api';
import { UserManagementService } from '$State/UserManagementFreezerService';
import * as DateFormatter from '$Components/Shared/DateFormatter';
const InjectedPropName = 'videoRecall';

interface IVideoRecallState {
    videoRecallResults: IAjaxState<VideoEventResponseIEnumerablePagedResponseBase>;
    requestVideoRecallResult: IAjaxState<void>;
    hasError: boolean;
    hasInfiniteScrollError: boolean;
    videoRecallResultsFinal: VideoEventResponse[];
    videoRecallViewType: 'tile' | 'list';
    promoteVideoRecallResult: IAjaxState<void>;
    videosLoaded: boolean;
    includeInactiveVehicles: boolean;
}

class VideoRecallFreezerService extends FreezerService<IVideoRecallState, typeof InjectedPropName> {
    constructor() {
        super(
            {
                videoRecallResults: managedAjaxUtil.createInitialState(),
                requestVideoRecallResult: managedAjaxUtil.createInitialState(),
                hasError: false,
                hasInfiniteScrollError: false,
                videoRecallResultsFinal: [],
                videoRecallViewType: 'tile',
                promoteVideoRecallResult: managedAjaxUtil.createInitialState(),
                videosLoaded: false,
                includeInactiveVehicles: false,
            },
            InjectedPropName,
        );
    }

    public async getFilteredVideoRecalls(
        filterOptions: VideoEventFilterRequest,
        options?: { append?: boolean },
    ): Promise<void | VideoEventResponseIEnumerablePagedResponseBase> {
        return managedAjaxUtil.fetchResults({
            ajaxStateProperty: 'videoRecallResults',
            freezer: this.freezer,
            onExecute: (apiOptions, params) => {
                this.freezer.get().set({ videosLoaded: false });
                if (params === undefined) {
                    throw new Error('Parameters is undefined');
                }

                const VideoEventApi = VideoEventApiFactory(apiOptions.wrappedFetch, apiOptions.baseUrl);
                return VideoEventApi.apiV1VideorecallFilteredeventsPost(params, apiOptions.fetchOptions);
            },
            params: {
                ...UserManagementService.generateIntegrationUserParams(),
                ...{},
                body: filterOptions,
            },
            onOk: (data: VideoEventResponseIEnumerablePagedResponseBase) => {
                this.freezer.get().set({ videosLoaded: true });
                return this.transformVideoRecallResults(data, options?.append);
            },
            onError: () => {
                this.freezer.get().set({ hasError: true });
            },
        });
    }

    public async requestVideoRecall(request: VideoEventResponse): Promise<void> {
        return managedAjaxUtil.fetchResults({
            ajaxStateProperty: 'requestVideoRecallVideoResult',
            freezer: this.freezer,
            onExecute: (apiOptions, params) => {
                if (params === undefined) {
                    throw new Error('Parameters is undefined');
                }

                const VideoEventApi = VideoEventApiFactory(apiOptions.wrappedFetch, apiOptions.baseUrl);
                return VideoEventApi.apiV1RecallvideoPost(params, apiOptions.fetchOptions);
            },
            params: {
                ...UserManagementService.generateIntegrationUserParams(),
                ...{
                    integrationPartnerVehicleId: request.integrationPartnerVehicleId,
                    startDate: DateFormatter.getUtcString(moment(request.eventStartDate)),
                    endDate: DateFormatter.getUtcString(moment(request.eventEndDate)),
                },
            },
            onOk: () => {
                // noop, no data returned
            },
            onError: () => {
                this.freezer.get().set({ hasError: true });
            },
        });
    }

    public setVideoRecallViewType(type: 'tile' | 'list'): void {
        this.freezer.get().set({ videoRecallViewType: type });
    }

    public async deleteVideoRecall(eventId: number): Promise<void | VideoEventResponseIEnumerablePagedResponseBase> {
        return managedAjaxUtil.fetchResults({
            ajaxStateProperty: 'deleteVideoRecallResult',
            freezer: this.freezer,
            onExecute: (apiOptions, params) => {
                if (params === undefined) {
                    throw new Error('Parameters is undefined');
                }

                const VideoEventApi = VideoEventApiFactory(apiOptions.wrappedFetch, apiOptions.baseUrl);
                return VideoEventApi.apiV1VideorecallDeletevideorecallPost(params, apiOptions.fetchOptions);
            },
            params: {
                ...UserManagementService.generateIntegrationUserParams(),
                ...{ eventId: eventId },
            },
            onOk: () => {
                // no-op
            },
            onError: () => {
                this.freezer.get().set({ hasError: true });
            },
        });
    }

    private transformVideoRecallResults(data: VideoEventResponseIEnumerablePagedResponseBase, append?: boolean): any {
        if (data.data !== undefined && data.success) {
            this.freezer.get().set({ hasError: false, hasInfiniteScrollError: false });
        } else {
            this.freezer.get().set({ hasError: true, hasInfiniteScrollError: true });
        }

        if (data.data) {
            if (append) {
                const existing = this.freezer.get().videoRecallResultsFinal;
                this.freezer.get().set({ videoRecallResultsFinal: [...existing, ...data.data] as VideoEventResponse[] });
            } else {
                this.freezer.get().set({ videoRecallResultsFinal: [...data.data] });
            }
        }

        return data;
    }

    public async PromoteVideoRecall(eventId: number | undefined): Promise<void> {
        return managedAjaxUtil.fetchResults({
            ajaxStateProperty: 'promoteVideoRecallResult',
            freezer: this.freezer,
            onExecute: (apiOptions, params) => {
                if (params === undefined) {
                    throw new Error('Parameters is undefined');
                }

                const VideoEventApi = VideoEventApiFactory(apiOptions.wrappedFetch, apiOptions.baseUrl);
                return VideoEventApi.apiV1PromotevideorecallPost(params, apiOptions.fetchOptions);
            },
            params: {
                ...UserManagementService.generateIntegrationUserParams(),
                ...{ eventId: eventId },
            },
            onOk: () => {
                // noop, no data returned
            },
            onError: () => {
                this.freezer.get().set({ hasError: true });
            },
        });
    }

    public getVideosLoaded(): boolean {
        return this.freezer.get().videosLoaded;
    }

    public setIncludeInactiveVehicles(includeInactiveVehicles?: boolean) {
        this.freezer.get().set({ includeInactiveVehicles });
    }

    public getIncludeInactiveVehicles(): boolean {
        return this.getState().includeInactiveVehicles;
    }

    // Updates the matching card's status in place -- mirrors VideoEventFreezerService's
    // updateLocalVideoEventStatus -- so the card list reflects a play-triggered status change
    // immediately without a re-fetch of the (potentially large, filtered/paged) card list.
    public updateLocalVideoEventStatus(status: VideoEventWorkflowStatusIdEnum, eventId: number | undefined): void {
        const results = this.freezer.get().videoRecallResultsFinal;
        for (let i = 0; i < results.length; i++) {
            if (results[i].id === eventId) {
                results[i].set({ videoEventStatus: status });
                break;
            }
        }
    }
}

export const VideoRecallService = new VideoRecallFreezerService();
export type IVideoRecallServiceInjectedProps = ReturnType<VideoRecallFreezerService['getPropsForInjection']>;
